import type { AgeVerificationStatus } from '@/services/auth/AgeVerificationService';
import type { AuthResolution } from '@/utils/authSessionGuard';

export type AgeSessionStatus = 'checking' | AgeVerificationStatus;

export type AgeGateView = 'gate' | 'checking' | 'app';

export type AgeVerificationPlan = {
  shouldFetch: boolean;
  status?: AgeSessionStatus;
  userId?: string | null;
  bumpGeneration?: boolean;
};

/**
 * Decide whether a user-id / auth-resolution change should show checking,
 * keep a visible gate, or reset after a completed logout.
 */
export function planAgeVerificationFetch(args: {
  previousUserId: string | null;
  nextUserId: string | null;
  currentStatus: AgeSessionStatus;
  authResolution: AuthResolution;
}): AgeVerificationPlan {
  if (!args.nextUserId) {
    if (args.authResolution === 'logged_out') {
      return {
        shouldFetch: false,
        status: 'checking',
        userId: null,
        bumpGeneration: true,
      };
    }
    // Hydration / token-refresh flicker: keep the currently rendered gate (or verified shell).
    return { shouldFetch: false };
  }

  const userChanged = args.previousUserId !== args.nextUserId;
  if (userChanged) {
    return {
      shouldFetch: true,
      status: 'checking',
      userId: args.nextUserId,
      bumpGeneration: true,
    };
  }

  if (args.currentStatus === 'verified') {
    return { shouldFetch: false };
  }

  // Same user, already resolved to the gate (or still on first checking): revalidate
  // without forcing checking so a visible not_asked/denied overlay is not replaced.
  return { shouldFetch: true };
}

/**
 * Apply an async age result only if it belongs to the newest request for the
 * active user. Ignores previous users, older generations, and unsafe overwrites.
 */
export function acceptAgeVerificationResult(args: {
  requestUserId: string;
  activeUserId: string | null;
  requestGeneration: number;
  activeGeneration: number;
  currentStatus: AgeSessionStatus;
  incoming: AgeVerificationStatus;
}): AgeVerificationStatus | null {
  if (args.requestGeneration !== args.activeGeneration) return null;
  if (args.requestUserId !== args.activeUserId) return null;
  if (args.currentStatus === 'verified') return null;
  if (args.currentStatus === 'denied' && args.incoming === 'not_asked') return null;
  return args.incoming;
}

/**
 * Shared render split used by App.tsx. Priority:
 * 1. Visible not_asked/denied gate while not genuinely logged out (includes null-user flicker)
 * 2. Checking fallback only for a live user before the first result (not bypass, not OAuth callback)
 * 3. App routes: public after logout, verified session, bypass, or OAuth callback while checking
 */
export function selectAgeGateView(opts: {
  userId: string | null;
  status: AgeSessionStatus;
  authResolution: AuthResolution;
  ageGateBypass: boolean;
  isAuthCallbackRoute: boolean;
}): AgeGateView {
  if (
    (opts.status === 'not_asked' || opts.status === 'denied') &&
    opts.authResolution !== 'logged_out'
  ) {
    return 'gate';
  }

  if (
    !!opts.userId &&
    opts.status === 'checking' &&
    !opts.ageGateBypass &&
    !opts.isAuthCallbackRoute
  ) {
    return 'checking';
  }

  return 'app';
}
